import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable item component
const SortableFieldItem = ({ field, index, onUpdate, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || `field-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg mb-2"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-move text-gray-400 hover:text-gray-600"
      >
        ⋮⋮
      </div>
      <div className="flex-1 grid grid-cols-4 gap-4">
        <input
          type="text"
          placeholder="Field Name"
          value={field.name}
          onChange={(e) => onUpdate(index, 'name', e.target.value)}
          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        <select
          value={field.type}
          onChange={(e) => onUpdate(index, 'type', e.target.value)}
          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="select">Select</option>
          <option value="boolean">Boolean</option>
          <option value="date">Date</option>
        </select>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={field.required || false}
            onChange={(e) => onUpdate(index, 'required', e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">Required</span>
        </label>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-red-600 hover:text-red-800 font-medium"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

const DynamicAssetForm = ({ assetType, onSubmit, initialData }) => {
  const { register, handleSubmit, setValue, watch } = useForm({ defaultValues: initialData });
  const [customFields, setCustomFields] = useState([]);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Fetch custom fields for this asset type
    fetchCustomFields(assetType).then(fields => {
      // Add unique IDs to fields for drag-and-drop
      const fieldsWithIds = fields.map((field, idx) => ({
        ...field,
        id: `field-${Date.now()}-${idx}`
      }));
      setCustomFields(fieldsWithIds);
    });
  }, [assetType]);

  const addCustomField = () => {
    const newField = {
      id: `field-${Date.now()}-${customFields.length}`,
      name: '',
      type: 'text',
      required: false
    };
    setCustomFields([...customFields, newField]);
  };

  const removeCustomField = (index) => {
    const newFields = [...customFields];
    newFields.splice(index, 1);
    setCustomFields(newFields);
  };

  const updateCustomField = (index, fieldName, value) => {
    const newFields = [...customFields];
    newFields[index][fieldName] = value;
    setCustomFields(newFields);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setCustomFields((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const renderField = (field, index) => {
    switch (field.type) {
      case 'number':
        return (
          <input
            type="number"
            {...register(field.name)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            {...register(field.name)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required={field.required}
          >
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case 'boolean':
        return (
          <input
            type="checkbox"
            {...register(field.name)}
            className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
        );
      default:
        return (
          <input
            type="text"
            {...register(field.name)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required={field.required}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Basic Information
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Core asset details that apply to all types
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <div className="grid grid-cols-6 gap-6">
  {/* Asset Name */}
  <div className="col-span-6 sm:col-span-3">
    <label className="block text-sm font-medium text-gray-700">
      Asset Name
    </label>
    <input
      type="text"
      {...register('name', { required: true })}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
    />
  </div>

  {/* Category (NEW - FIXED) */}
  <div className="col-span-6 sm:col-span-3">
    <label className="block text-sm font-medium text-gray-700">
      Category
    </label>
    <select
      {...register('category', { required: true })}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
    >
      <option value="">Select Category</option>
      <option value="property">Property / Building</option>
      <option value="vehicle">Vehicle</option>
      <option value="equipment">Equipment</option>
      <option value="furniture">Furniture</option>
      <option value="other">Other</option>
    </select>
  </div>

  {/* Base Rate */}
  <div className="col-span-6 sm:col-span-3">
    <label className="block text-sm font-medium text-gray-700">
      Base Rate (KES)
    </label>
    <input
      type="number"
      {...register('base_rate', { required: true })}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
    />
  </div>

  {/* Description */}
  <div className="col-span-6">
    <label className="block text-sm font-medium text-gray-700">
      Description
    </label>
    <textarea
      {...register('description')}
      rows={3}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
    />
  </div>

  {/* Billing Cycle */}
  <div className="col-span-6 sm:col-span-3">
    <label className="block text-sm font-medium text-gray-700">
      Default Billing Cycle
    </label>
    <select
      {...register('billing_cycle')}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
    >
      <option value="monthly">Monthly</option>
      <option value="weekly">Weekly</option>
      <option value="daily">Daily</option>
      <option value="termly">Termly (School Terms)</option>
      <option value="milestone">Milestone (One-time)</option>
    </select>
    <p className="mt-1 text-xs text-gray-500">New leases on this asset will use this billing cycle by default</p>
  </div>
</div>
          </div>
        </div>
      </div>

      {/* Custom Fields (Dynamic) */}
      <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Custom Specifications
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Add type-specific attributes for this asset
            </p>
            <button
              type="button"
              onClick={addCustomField}
              className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              + Add Field
            </button>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={customFields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {customFields.map((field, index) => (
                  <SortableFieldItem
                    key={field.id}
                    field={field}
                    index={index}
                    onUpdate={updateCustomField}
                    onRemove={removeCustomField}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {customFields.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                No custom fields added yet. Click "+ Add Field" to start.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Save Asset
        </button>
      </div>
    </form>
  );
};

// Temporary helper function - replace with actual API call
const fetchCustomFields = async (assetType) => {
  // Mock data - replace with your actual API call
  return [];
};

export default DynamicAssetForm;